import React, { useState, useEffect } from 'react';
import { useUser } from 'util/UserContext';
import Modal from 'ui/Modal';
import { Drawer, DrawerContent } from 'ui/Drawer';
import api from 'config/axiosConfig';
import FollowButton from './FollowButton';
import EntityAvatar from 'components/Entities/EntityAvatar';
import LoadingSkeleton from 'ui/LoadingSkeleton';
import { getActorDetails } from 'util/getObjectDetails';

interface Follower {
    id: number;
    follower_id: number;
    follower_type: 'user' | 'team';
    following_id: number;
    following_type: 'user' | 'team';
    username?: string;
    teamname?: string;
    follow_entity?: any;
}

interface Props {
    entityId: number;
    entityType: 'user' | 'team';
    viewType: 'followers' | 'following';
    onClose: () => void;
}

const FollowersModal: React.FC<Props> = ({ entityId, entityType, viewType, onClose }) => {
    const { userIsMobile, currentUser } = useUser();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [enrichedFollowers, setEnrichedFollowers] = useState<any[]>([]);
    const [isDrawerVisible, setIsDrawerVisible] = useState(true);


    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch followers/following
                const response = await api.get(
                    viewType === 'followers'
                        ? `/api/follow/${entityType}/${entityId}/followers`
                        : `/api/follow/${entityType}/${entityId}/following`
                );

                // Enrich the followers data
                const enriched = await Promise.all(response.data.map(async (follower: Follower) => {
                    const followerId = viewType === 'followers' ? follower.follower_id : follower.following_id;
                    const followerType = viewType === 'followers' ? follower.follower_type : follower.following_type;
                    
                    const enrichedData = await getActorDetails(followerType, followerId);
                    
                    return {
                        ...follower,
                        id: followerId,
                        type: followerType,
                        follow_entity: enrichedData
                    };
                }));

                setEnrichedFollowers(enriched);
            } catch (error) {
                console.error('Error fetching followers:', error);
                setError('Failed to load followers.');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [entityId, entityType, viewType]);

    const handleDialogOpen = () => {
        setIsDrawerVisible(false);
    };

    const handleDialogClose = () => {
        setIsDrawerVisible(true);
    };

    const content = (
        <div className="flex flex-col space-y-4">
            {loading ? (
                <LoadingSkeleton count={5} height="h-16" />
            ) : error ? (
                <div className="text-warning-red dark:text-warning-red-dark text-center p-4">
                    {error}
                </div>
            ) : enrichedFollowers.length === 0 ? (
                <div className="text-eoe-text-sub dark:text-eoe-dark-text-sub text-center p-4">
                    {viewType === 'followers' ? 'No followers' : 'Not following anyone'} yet
                </div>
            ) : (
                <div className="space-y-2">
                    {enrichedFollowers.map((follower) => (
                        <div key={`${follower.type}-${follower.id}`} className="flex items-center justify-between p-2">
                            <EntityAvatar
                                entity={follower.follow_entity}
                                entityType={follower.type}
                                entityChip={true}
                                showCloseChip={false}
                                isInputField={false}
                                showLink={true}
                            />
                            {(viewType === 'followers' && follower.follower_id !== currentUser?.id) && (
                                <FollowButton
                                    entityId={follower.follower_id}
                                    entityType={follower.follower_type}
                                    entityName={follower.follow_entity?.username || follower.follow_entity?.teamname}
                                    isPrivate={follower.follow_entity?.properties_public?.is_private}
                                    onDialogOpen={handleDialogOpen}
                                    onDialogClose={handleDialogClose}
                                />
                            )}
                            {(viewType === 'following' && follower.following_id !== currentUser?.id) && (
                                <FollowButton
                                    entityId={follower.following_id}
                                    entityType={follower.following_type}
                                    entityName={follower.follow_entity?.username || follower.follow_entity?.teamname}
                                    isPrivate={follower.follow_entity?.properties_public?.is_private}
                                    onDialogOpen={handleDialogOpen}
                                    onDialogClose={handleDialogClose}
                                />
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    return (
        <>
            {userIsMobile ? (
                isDrawerVisible ? (
                    <Drawer open={true} onOpenChange={onClose}>
                        <DrawerContent title={`${viewType === 'followers' ? 'Followers' : 'Following'}`}>
                            {content}
                        </DrawerContent>
                    </Drawer>
                ) : null
            ) : (
                <Modal 
                    isOpen={true} 
                    onClose={onClose} 
                    title={`${viewType === 'followers' ? 'Followers' : 'Following'}`}
                >
                    {content}
                </Modal>
            )}
        </>
    );
};

export default FollowersModal; 